import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, GraduationCap, ArrowRight, BookOpen, Briefcase, Award, ChevronRight, Facebook, MessageCircle, Sparkles, Youtube } from 'lucide-react';
import { personalInfo, achievements, socialLinks as socialLinksData } from '../../data/mock';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const quickLinks = [
    { label: 'Home', path: '/' },
    { label: 'About Me', path: '/about' },
    { label: 'Experience', path: '/experience' },
    { label: 'Publications', path: '/publications' },
    { label: 'Projects', path: '/projects' },
    { label: 'Gallery', path: '/gallery' },
  ];

  const services = [
    { label: 'Research Consultation', path: '/contact' },
    { label: 'Academic Supervision', path: '/contact' },
    { label: 'Institutional Assessment', path: '/projects' },
    { label: 'M&E Systems Design', path: '/projects' },
    { label: 'Training Programs', path: '/projects' },
  ];

  const expertise = [
    'Sociology & Development',
    'Governance & Elections',
    'Research Methodology',
    'Project Management',
    'Data Analytics',
    'Academic Writing',
  ];

  // TikTok Icon Component
  const TikTokIcon = () => (
    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
    </svg>
  );

  const socialLinks = [
    { icon: Facebook, label: 'Facebook', href: 'https://www.facebook.com/saki.bade.5', color: 'from-[#1877F2] to-[#0D65D9]' },
    { icon: MessageCircle, label: 'WhatsApp', href: 'https://wa.me/252634453948', color: 'from-[#25D366] to-[#128C7E]' },
    { icon: Youtube, label: 'YouTube', href: socialLinksData.youtube, color: 'from-red-500 to-pink-500' },
    { icon: TikTokIcon, label: 'TikTok', href: socialLinksData.tiktok, color: 'from-gray-800 to-gray-900', isCustom: true },
  ];

  return (
    <footer className="relative overflow-hidden">
      {/* Decorative Top Wave */}
      <div className="absolute top-0 left-0 right-0 h-24 bg-white">
        <svg className="absolute bottom-0 w-full h-24" viewBox="0 0 1440 96" preserveAspectRatio="none">
          <path fill="#0F1F3D" d="M0,64 C360,96 720,32 1080,64 C1260,80 1380,48 1440,64 L1440,96 L0,96 Z" />
        </svg>
      </div>

      {/* Main Footer */}
      <div className="bg-[#0F1F3D] pt-28 pb-0">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          
          {/* Stats Row - Compact Colorful Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-14">
            {achievements.map((stat, index) => {
              const colors = [
                { gradient: 'from-emerald-400 to-green-600', shadow: 'shadow-emerald-500/30' },
                { gradient: 'from-amber-400 to-orange-500', shadow: 'shadow-amber-500/30' },
                { gradient: 'from-blue-400 to-indigo-600', shadow: 'shadow-blue-500/30' },
                { gradient: 'from-purple-400 to-violet-600', shadow: 'shadow-purple-500/30' },
              ];
              const color = colors[index % colors.length];
              return (
                <div 
                  key={index} 
                  className={`text-center p-4 rounded-2xl bg-gradient-to-br ${color.gradient} shadow-lg ${color.shadow} hover:scale-105 hover:-translate-y-1 transition-all duration-500 cursor-pointer`}
                >
                  <p className="text-3xl md:text-4xl font-black text-white" style={{ fontFamily: "'Montserrat', sans-serif" }}>{stat.number}</p>
                  <p className="text-white/80 text-xs font-bold mt-1 uppercase tracking-wider">{stat.label}</p>
                </div>
              );
            })}
          </div>

          {/* Main Content Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-10 mb-12">
            
            {/* Brand Column */}
            <div className="lg:col-span-4">
              <div className="flex items-center gap-3 mb-6">
                <div className="relative">
                  <div className="w-14 h-14 bg-gradient-to-br from-amber-400 to-orange-500 rounded-xl flex items-center justify-center shadow-lg shadow-amber-500/30">
                    <GraduationCap className="w-7 h-7 text-white" />
                  </div>
                  <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-gradient-to-br from-emerald-400 to-green-600 rounded-full border-2 border-[#0F1F3D] flex items-center justify-center shadow-md">
                    <span className="text-white text-[8px]">✓</span>
                  </div>
                </div>
                <div>
                  <h3 className="text-white font-black text-lg" style={{ fontFamily: "'Montserrat', sans-serif" }}>Dr. Zakariye Abdi Bade</h3>
                  <p className="text-amber-400 font-bold text-xs">PhD in Sociology</p>
                </div>
              </div>
              <p className="text-gray-400 leading-relaxed mb-5 font-medium text-xs">
                Academic leader, researcher, and consultant with over a decade of experience advancing sociological research, governance, and institutional development.
              </p>
              
              {/* Contact Cards - Compact 2x2 Grid */}
              <div className="grid grid-cols-2 gap-2">
                <a
                  href={`mailto:${personalInfo.email}`}
                  className="flex items-center gap-3 p-3 rounded-xl bg-gradient-to-r from-blue-400 to-indigo-600 shadow-lg shadow-blue-500/20 hover:scale-105 transition-all duration-300 group"
                >
                  <div className="w-9 h-9 rounded-lg bg-white/20 flex items-center justify-center group-hover:scale-110 transition-transform">
                    <Mail className="w-4 h-4 text-white" />
                  </div>
                  <div className="overflow-hidden">
                    <p className="text-white/70 text-[10px] font-medium">Email</p>
                    <p className="text-white font-bold text-xs truncate">dr.zaki@dr-zakibade.com</p>
                  </div>
                </a>
                <a
                  href={`tel:${personalInfo.phone}`}
                  className="flex items-center gap-3 p-3 rounded-xl bg-gradient-to-r from-emerald-400 to-green-600 shadow-lg shadow-emerald-500/20 hover:scale-105 transition-all duration-300 group"
                >
                  <div className="w-9 h-9 rounded-lg bg-white/20 flex items-center justify-center group-hover:scale-110 transition-transform">
                    <Phone className="w-4 h-4 text-white" />
                  </div>
                  <div>
                    <p className="text-white/70 text-[10px] font-medium">Phone</p>
                    <p className="text-white font-bold text-xs">{personalInfo.phone}</p>
                  </div>
                </a>
                <a
                  href="https://wa.me/252634453948"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 p-3 rounded-xl bg-gradient-to-r from-[#25D366] to-[#128C7E] shadow-lg shadow-green-500/20 hover:scale-105 transition-all duration-300 group"
                >
                  <div className="w-9 h-9 rounded-lg bg-white/20 flex items-center justify-center group-hover:scale-110 transition-transform">
                    <MessageCircle className="w-4 h-4 text-white" />
                  </div>
                  <div>
                    <p className="text-white/70 text-[10px] font-medium">WhatsApp</p>
                    <p className="text-white font-bold text-xs">+252 63 4453 948</p>
                  </div>
                </a>
                <div className="flex items-center gap-3 p-3 rounded-xl bg-gradient-to-r from-amber-400 to-orange-500 shadow-lg shadow-amber-500/20">
                  <div className="w-9 h-9 rounded-lg bg-white/20 flex items-center justify-center">
                    <MapPin className="w-4 h-4 text-white" />
                  </div>
                  <div>
                    <p className="text-white/70 text-[10px] font-medium">Location</p>
                    <p className="text-white font-bold text-xs">Hargeisa</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Quick Links */}
            <div className="lg:col-span-2">
              <h4 className="text-white font-black text-xs uppercase tracking-wider mb-4 flex items-center gap-2" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                <span className="w-8 h-0.5 bg-gradient-to-r from-emerald-400 to-green-600 rounded"></span>
                Quick Links
              </h4>
              <nav className="space-y-2">
                {quickLinks.map((item) => (
                  <Link
                    key={item.label}
                    to={item.path}
                    className="flex items-center gap-2 text-gray-400 hover:text-white transition-all duration-300 group font-medium text-xs"
                  >
                    <ChevronRight className="w-3 h-3 text-emerald-400 group-hover:translate-x-1 transition-transform" />
                    {item.label}
                  </Link>
                ))}
              </nav>
            </div>

            {/* Services */}
            <div className="lg:col-span-3">
              <h4 className="text-white font-black text-xs uppercase tracking-wider mb-4 flex items-center gap-2" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                <span className="w-8 h-0.5 bg-gradient-to-r from-amber-400 to-orange-500 rounded"></span>
                Services
              </h4>
              <nav className="space-y-2">
                {services.map((item) => (
                  <Link
                    key={item.label}
                    to={item.path}
                    className="flex items-center gap-2 text-gray-400 hover:text-white transition-all duration-300 group font-medium text-xs"
                  >
                    <ChevronRight className="w-3 h-3 text-amber-400 group-hover:translate-x-1 transition-transform" />
                    {item.label}
                  </Link>
                ))}
              </nav>
            </div>

            {/* Expertise & Social */}
            <div className="lg:col-span-3">
              <h4 className="text-white font-black text-xs uppercase tracking-wider mb-4 flex items-center gap-2" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                <span className="w-8 h-0.5 bg-gradient-to-r from-purple-400 to-violet-600 rounded"></span>
                Expertise
              </h4>
              <div className="grid grid-cols-2 gap-1.5 mb-6">
                {expertise.map((item, index) => {
                  const colors = ['bg-emerald-500/20 text-emerald-300', 'bg-amber-500/20 text-amber-300', 'bg-blue-500/20 text-blue-300', 'bg-purple-500/20 text-purple-300', 'bg-rose-500/20 text-rose-300', 'bg-cyan-500/20 text-cyan-300'];
                  return (
                    <span
                      key={index}
                      className={`px-2 py-1.5 text-[10px] font-bold ${colors[index % colors.length]} rounded-lg hover:scale-105 transition-transform cursor-pointer text-center`}
                    >
                      {item}
                    </span>
                  );
                })}
              </div>
              
              {/* Social Links */}
              <h4 className="text-white font-black text-xs uppercase tracking-wider mb-3 flex items-center gap-2" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                <span className="w-8 h-0.5 bg-gradient-to-r from-blue-400 to-indigo-600 rounded"></span>
                Follow Me
              </h4>
              <div className="flex gap-2">
                {socialLinks.map((social, index) => (
                  <a
                    key={index}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`w-10 h-10 rounded-xl bg-gradient-to-br ${social.color} flex items-center justify-center shadow-lg hover:scale-110 hover:-translate-y-1 transition-all duration-300`}
                  >
                    {social.isCustom ? <social.icon /> : <social.icon className="w-5 h-5 text-white" />}
                  </a>
                ))}
              </div>
              
              {/* CTA Button */}
              <Link
                to="/contact"
                className="mt-5 inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-amber-400 to-orange-500 text-white font-bold text-sm rounded-xl shadow-lg shadow-amber-500/30 hover:scale-105 hover:-translate-y-1 transition-all duration-300"
              >
                Get in Touch
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>

          {/* Bottom Bar - Stylish Design */}
          <div className="relative">
            {/* Top Decorative Line */}
            <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/20 to-transparent" />
            
            <div className="pt-6 pb-6">
              {/* Main Bottom Content */}
              <div className="flex flex-col lg:flex-row items-center justify-between gap-6">
                
                {/* Left - Copyright & Branding */}
                <div className="flex flex-col sm:flex-row items-center gap-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-amber-400 to-orange-500 rounded-xl flex items-center justify-center shadow-lg">
                      <span className="text-white font-black text-sm">ZB</span>
                    </div>
                    <div>
                      <p className="text-white font-bold" style={{ fontFamily: "'Montserrat', sans-serif" }}>Dr. Zaki Bade</p>
                      <p className="text-gray-500 text-xs">Academic Excellence</p>
                    </div>
                  </div>
                  <div className="hidden sm:block w-px h-8 bg-white/10" />
                  <p className="text-gray-500 text-sm font-medium">
                    © {currentYear} All rights reserved.
                  </p>
                </div>

                {/* Center - Role Tags */}
                <div className="flex flex-wrap justify-center items-center gap-3">
                  {['Sociologist', 'Academic Leader', 'Researcher', 'Consultant'].map((role, index) => {
                    const colors = ['from-emerald-400 to-green-600', 'from-amber-400 to-orange-500', 'from-blue-400 to-indigo-600', 'from-purple-400 to-violet-600'];
                    return (
                      <span 
                        key={role}
                        className={`px-4 py-2 bg-gradient-to-r ${colors[index]} text-white text-xs font-bold rounded-full shadow-lg hover:scale-105 transition-transform cursor-pointer`}
                      >
                        {role}
                      </span>
                    );
                  })}
                </div>
              </div>

              {/* Bottom Decorative Elements */}
              <div className="flex justify-center mt-8">
                <div className="flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-amber-400" />
                  <span className="text-gray-600 text-xs font-medium">Empowering Communities Through Research & Education</span>
                  <Sparkles className="w-4 h-4 text-amber-400" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
